// SystemDetail.tsx — The focused star's planetary system: a bright point light at
// the star (so it physically shades the planets), the elliptical orbit paths, an
// optional asteroid belt, and the planets (each with terrain, shells, and moons).
// Mounted at the star's galaxy position so the fly-in lands on it.
import { useMemo } from 'react';
import { Line } from '@react-three/drei';
import { Vector3 } from 'three';
import { SPECTRAL } from '../galaxy/spectral';
import { useGame } from '../state/context';
import type { Galaxy, Planet as PlanetData, StarSystem } from '../types';
import { Planet } from './Planet';
import { AsteroidBelt } from './AsteroidBelt';
import { Singularity } from './Singularity';
import { SystemShips } from './SystemShips';
import { gatesOf } from './hyperlane';

interface Props {
  galaxy: Galaxy;
  system: StarSystem;
}

// Sample one full ellipse (star at a focus) in the planet's orbital plane,
// rotated by its argument of periapsis — matches orbit.ts's perifocal mapping.
function orbitPath(p: PlanetData): Vector3[] {
  const b = p.semiMajorAxis * Math.sqrt(1 - p.eccentricity * p.eccentricity);
  const cosW = Math.cos(p.longitudeOfPeriapsis);
  const sinW = Math.sin(p.longitudeOfPeriapsis);
  const pts: Vector3[] = [];
  const segments = 128;
  for (let k = 0; k <= segments; k++) {
    const e = (k / segments) * Math.PI * 2;
    const xp = p.semiMajorAxis * (Math.cos(e) - p.eccentricity);
    const zp = b * Math.sin(e);
    pts.push(new Vector3(xp * cosW - zp * sinW, 0, xp * sinW + zp * cosW));
  }
  return pts;
}

export function SystemDetail({ galaxy, system }: Props) {
  const { focusedPlanetId } = useGame();
  const info = SPECTRAL[system.spectralClass];
  // The star sits at this system group's origin, so its world position is just
  // system.position — thread it to the planets for their atmosphere/ocean shaders.
  const starWorldPos = useMemo(() => new Vector3(...system.position), [system.position]);
  const orbits = useMemo(
    () => system.planets.map((p) => ({ id: p.id, inclination: p.inclination, points: orbitPath(p) })),
    [system],
  );
  // Hyperlane mouths ride the system's rim, aimed at the systems they connect to.
  const gates = useMemo(() => gatesOf(galaxy, system.id), [galaxy, system.id]);
  // One shared rim radius set BEYOND every object in the system — the farthest
  // planet's aphelion plus its widest moon, the belt's outer edge — with a margin,
  // so the singularities are always the outermost ring and all equidistant.
  const rim = useMemo(() => {
    let max = system.radius * 2;
    for (const p of system.planets) {
      let moonReach = 0;
      for (const m of p.moons ?? []) {
        moonReach = Math.max(moonReach, m.semiMajorAxis * (1 + m.eccentricity) + m.radius);
      }
      max = Math.max(max, p.semiMajorAxis * (1 + p.eccentricity) + p.radius + moonReach);
    }
    if (system.asteroidBelt) {
      max = Math.max(max, system.asteroidBelt.outerRadius);
    }
    return max * 1.3;
  }, [system]);

  return (
    <group position={system.position}>
      <pointLight color={info.light} intensity={200} distance={120} decay={2} />
      {orbits.map((o) => (
        <group key={o.id} rotation={[o.inclination, 0, 0]}>
          <Line points={o.points} color="#46587e" transparent opacity={0.4} lineWidth={1} />
        </group>
      ))}
      {system.asteroidBelt ? <AsteroidBelt belt={system.asteroidBelt} /> : null}
      {system.planets.map((planet) => (
        <Planet
          key={planet.id}
          planet={planet}
          system={system}
          starWorldPos={starWorldPos}
          starRadius={system.radius}
        />
      ))}
      {/* Freighters flying their courses in this system (3D, course-local to the star). */}
      <SystemShips galaxy={galaxy} system={system} />
      {/* Points of singularity — only on the open system view, not when a planet is
          focused (they'd be distant clutter behind the planet). */}
      {focusedPlanetId === null
        ? gates.map((gate) => (
            <Singularity key={gate.id} gate={gate} systemPos={system.position} rim={rim} />
          ))
        : null}
    </group>
  );
}
